// ═══════════════════════════════════════════════════════════════════════════
// AI dispatch-context builders (CC-2d)
// ═══════════════════════════════════════════════════════════════════════════
//
// Pure helpers that project the LIVE editor state into the
// `AiDispatchContext` / `resolveInsertIds` inputs the safe contour
// (`aiSafeContour.ts`) needs. They take the document / shell state as
// PARAMETERS (no store import) so the dependency direction stays
// acyclic: `editorStore` (window mounts) and the CC-2d AI panel both
// import THIS module; this module never imports the store.
//
//   editorStore ──┐
//                 ├──> aiDispatchContext   (type-only imports; no runtime dep)
//   AiDraftingPanel ┘
//
// The snapshot readers (`collectAiInsertIds` / `collectAiEntitySnapshot`)
// project the LIVE `geometryDocument` the caller passes in in ONE linear pass
// total per snapshot — block-definition `blocks[name].entities` (ATTDEF) plus
// the space entities, the latter read via the cheapest authoritative source:
//
//   • Cache-backed doc (compact holder authoritative, `cachePopulatedOnce`):
//     ONE holder-wide `forEachCachedEntity` walk over the whole `idIndex` —
//     NOT the per-space read-through getter. The getter calls
//     `snapshotSpaceEntities(docId, space.id)`, each a FULL `idIndex` walk
//     filtered by ONE space; iterating every space ⇒ O(spaces × N), ×2 per AI
//     turn (before + after) ⇒ freeze on a large multi-layout streamed doc.
//     The holder-wide walk collapses that to O(N) (PR #398 @codex P2 round-2).
//
//   • Inline-fallback doc (holder absent / `cachePopulatedOnce === false`):
//     iterate each space's RAW inline `space.entities` array. The read-through
//     getter SHORT-CIRCUITS to this backing array WITHOUT a holder walk in this
//     state, so the per-space read is O(entities-in-space) with no full-cache
//     scan — preserving the P2-1 inline-correctness fix (inline entities ARE
//     captured, classified AFFECTED on a same-id fingerprint change).
//
// Authoritative-vs-inline is decided the SAME way the read-through wrapper
// (`spaceReadThrough.wrapSpaceAsReadThrough`) decides it: a holder that exists
// AND has taken a write through the cache path (`cachePopulatedOnce`). They no
// longer call the per-space compact reader (`snapshotSpaceEntities`), which had
// no inline fallback, dropped block-definition ATTDEF, and re-walked the whole
// holder per space (PR #398 @codex P2-1/P2-2/P2-3 round-1; the per-space
// read-through getter re-introduced the same per-space full-holder walk on
// cache-backed docs — P2 round-2).

import type { EditorShellState } from "@dxf-ai-editor/contracts";
import type { DrawingDocument } from "@dxf-ai-editor/drawing-model";
import type { AiDispatchContext } from "@dxf-ai-editor/ai-commands";

import {
  forEachCachedEntity,
  peekDocumentMemoryHolder,
} from "../state/mutationStore";

import type { AiEntitySnapshotEntry } from "./aiSafeContour";

/**
 * PR #398 @codex P2 (T2 round-2) — is the compact holder the authoritative
 * entity source for `docId`? Mirrors EXACTLY the predicate
 * `wrapSpaceAsReadThrough`'s getter uses to choose cache vs inline: a holder
 * that EXISTS (not minted-then-cleared) AND has taken at least one write
 * through the cache path (`cachePopulatedOnce`). When `false`, the
 * read-through getter returns the inline `space.entities` backing array
 * without a holder walk, so the caller must read inline directly.
 */
function isCacheAuthoritative(docId: string): boolean {
  const holder = peekDocumentMemoryHolder(docId);
  return holder !== undefined && holder.cachePopulatedOnce;
}

/**
 * Read the ids of every INSERT (Block) entity currently in the document,
 * in document order. Passed to the safe contour as `resolveInsertIds` so
 * it can resolve the `LAST_INSERT_ID_SENTINEL` placeholder on
 * INSERT_BLOCK attribute updates (diff before/after a create to find the
 * freshly-minted id).
 *
 * Reads via the cheapest authoritative source in ONE linear pass (PR #398
 * @codex P2 round-2): a cache-backed doc walks the holder ONCE
 * (`forEachCachedEntity`) instead of firing the per-space read-through getter
 * (each a full `idIndex` walk filtered by one space ⇒ O(spaces × N)); an
 * inline-backed doc (compact holder not authoritative) iterates the inline
 * `space.entities` backing array directly (the getter short-circuits to it
 * with no holder walk in that state). Reading
 * `snapshotSpaceEntities(document.id, space.id)` directly — the round-1
 * predecessor — bypassed the inline fallback and returned `[]` for inline-only
 * documents, so an INSERT_BLOCK-with-attributes against an inline doc could not
 * resolve its sentinel (P2-1, same root as the snapshot reader).
 */
export function collectAiInsertIds(document: DrawingDocument): string[] {
  const ids: string[] = [];
  if (isCacheAuthoritative(document.id)) {
    // Cache-backed — ONE holder-wide walk, no per-space getter.
    forEachCachedEntity(document.id, (entity) => {
      if (entity.type === "Block") ids.push(entity.id);
    });
  } else {
    // Inline-backed — read the inline `space.entities` backing directly (the
    // read-through getter short-circuits to it without a holder walk).
    for (const space of document.spaces) {
      for (const entity of space.entities) {
        if (entity.type === "Block") ids.push(entity.id);
      }
    }
  }
  return ids;
}

/**
 * Snapshot EVERY entity in the document as `{ id, fingerprint }` rows, in
 * document order. Passed to the safe contour as `snapshotEntities` so it can
 * surface the ids its dispatched commands CREATED (`createdEntityIds`) and the
 * existing entities a modifier TOUCHED (`affectedEntityIds`) — the T2
 * agent-editing create→read→erase loop (the agent addresses what it just made).
 *
 * The `fingerprint` is `JSON.stringify(entity)`, mirroring the canonical
 * `diffDocumentMutations` `entitiesShapeEqual` so the contour's create-vs-affect
 * classification matches the store's own `EntityMutation` diff exactly (CREATED ≡
 * `added`, AFFECTED ≡ `updated`).
 *
 * SOURCES — ONE linear pass total (O(Σ all entities)), never a per-space
 * full-holder re-walk:
 *
 *   1. Space entities, via the cheapest authoritative source:
 *      • Cache-backed doc (`cachePopulatedOnce`): ONE holder-wide
 *        `forEachCachedEntity(document.id, …)` walk over the whole `idIndex`
 *        — a single O(N) scan for ALL spaces. The round-1 revision iterated
 *        the per-space read-through getter, which on a cache-backed doc calls
 *        `snapshotSpaceEntities(docId, space.id)` PER space — each a full
 *        `idIndex` walk filtered by one spaceId — so iterating every space ⇒
 *        O(spaces × N), and before+after ⇒ 2·spaces full-store scans +
 *        per-entity JSON.stringify ⇒ freeze on a large multi-layout streamed
 *        doc (P2 round-2; the round-1 test missed it because its fixture used
 *        INLINE spaces, where the getter returns the backing array with no
 *        holder walk).
 *      • Inline-backed doc (holder not authoritative): iterate each space's
 *        RAW inline `space.entities` array. The read-through getter
 *        short-circuits to exactly this backing array with NO holder walk in
 *        this state, so a MOVE of an inline-only entity diffs present→present
 *        (AFFECTED), never EMPTY→present (the round-1 P2-1 correctness fix vs
 *        the original compact-only reader).
 *      Holder-wide vs cross-space order: `forEachCachedEntity` walks `idIndex`
 *      insertion order (cross-type, cross-space) — the diff is keyed by id, so
 *      order does not affect created/affected classification.
 *
 *   2. `document.blocks[name].entities` for every block definition. ATTDEF
 *      rows (CREATE_ATTDEF / UPDATE_ATTDEF) live HERE, NOT in `document.spaces`
 *      — and the compact space readers filter ATTDEF out (it is a block-child
 *      only) — so the old reader returned `createdEntityIds: []` for an
 *      `attdef-*` allocation, breaking follow-up ATTDEF actions that address by
 *      id (P2-2). Block-definition children are part of the document the
 *      dispatched commands mutate, so they belong in the before/after diff.
 *
 * DEDUPE — first occurrence wins, spaces before blocks. During a live BEDIT
 * session the block-edit canvas mirrors the block's NON-ATTDEF children into a
 * synthetic `block-edit` space under the SAME id (`hydrateBlockEditSpace`); a
 * raw spaces+blocks concatenation would emit that id TWICE with two different
 * fingerprints, double-counting it in the AFTER diff. Keying by id (spaces
 * first, which is the authoritative live canvas) collapses the mirror to one
 * row; ATTDEF, which is never mirrored into a space, is contributed solely by
 * the blocks pass.
 */
export function collectAiEntitySnapshot(
  document: DrawingDocument,
): AiEntitySnapshotEntry[] {
  const rows: AiEntitySnapshotEntry[] = [];
  const seen = new Set<string>();
  const pushRow = (entity: { id: string }): void => {
    if (seen.has(entity.id)) return;
    seen.add(entity.id);
    rows.push({ id: entity.id, fingerprint: JSON.stringify(entity) });
  };
  // 1. Space entities — authoritative for any id mirrored into a space, so they
  //    go first in the dedupe.
  if (isCacheAuthoritative(document.id)) {
    // Cache-backed — ONE holder-wide walk across ALL spaces (no per-space
    // read-through getter, which would re-walk the whole holder per space).
    forEachCachedEntity(document.id, pushRow);
  } else {
    // Inline-backed — iterate the inline `space.entities` backing directly (the
    // read-through getter short-circuits to it without a holder walk).
    for (const space of document.spaces) {
      for (const entity of space.entities) pushRow(entity);
    }
  }
  // 2. Block-definition entities (incl ATTDEF). Skip ids already surfaced via a
  //    space (the BEDIT mirror keeps the same id) so each id appears once.
  for (const blockName of Object.keys(document.blocks)) {
    for (const entity of document.blocks[blockName].entities) pushRow(entity);
  }
  return rows;
}

/**
 * Build the production `AiDispatchContext` from the shell state:
 *
 *   • `activeSpaceId` — where space-less creates (INSERT_BLOCK,
 *     LINE/CIRCLE/… base primitives) land, mirroring the UI default.
 *   • `paperSpaceLayoutIds` — ordered PAPER-space layout ids, the single
 *     source of truth the VIEW+SPACE translators (MSPACE / PSPACE / MVIEW)
 *     resolve against. Filtered on `kind === "layout"` exactly like the
 *     CLI `LAYOUT Set` resolver: synthetic block-edit / refedit sessions
 *     are projected to `kind: "model"` by the shell, so they are excluded.
 *
 * The optional dynamic-block name→id resolvers are deliberately NOT wired:
 * the dynamic translators route through name-keyed `properties/apply` /
 * `chprop/apply` payloads (`Visibility:<name>` / `Parameter:<name>` /
 * `LookupOutput:<name>`) that the reducer — the canonical owner of the
 * block dictionaries — resolves internally (fail-closed on unknown names).
 */
export function buildAiDispatchContext(
  state: EditorShellState,
): AiDispatchContext {
  return {
    activeSpaceId: state.drawing.activeSpaceId,
    paperSpaceLayoutIds: state.drawing.spaces
      .filter((space) => space.kind === "layout")
      .map((space) => space.id),
    // The shell's drawing-context mode (#292 P2): geometry creates in the
    // "viewport" context (MSPACE_VIA_VP — floating model space through an
    // active VP) route to MODEL space via `pickSpaceId` even though
    // `activeSpaceId` stays the hosting layout id (which MSPACE/PSPACE
    // layout resolution still needs).
    layoutContext: state.drawing.layoutContext,
  };
}

/**
 * Merge the derived default with a caller-supplied context override. A
 * caller field WINS only when it is DEFINED (it may override the active
 * space or supply name resolvers); the derived layout list / active space /
 * layout context FILL THE GAP when the caller omits them.
 * Explicitly-undefined override fields are SKIPPED (#292 P2): a plain
 * `{ ...derived, ...override }` spread would let
 * `{ paperSpaceLayoutIds: undefined }` clobber the derived list, so
 * `assertPaperSpaceLayoutTarget` skips validation and SET_LAYOUT
 * spaceId:"model" regresses fail-OPEN (#290-14 all over again) — undefined
 * means "not supplied", never "validate against nothing".
 *
 * `base` (Codex #342 F5): an optional second layer of derived/live values
 * (e.g. the live MIRRTEXT sysvar the window seam reads) applied via the SAME
 * defined-only merge AFTER the structural derivation but BEFORE the override.
 * Precedence is base < override: an EXPLICIT caller field still wins, and an
 * explicitly-`undefined` override field NEVER erases the `base` value (a plain
 * `{ ...base, ...override }` spread would, then the defined-only override loop
 * would skip it → the live value is lost). Both layers go through
 * `assignDefinedContextField`, so undefined never clobbers anything.
 *
 * SECURITY (CC-2b): this merges ONLY `context` — it NEVER derives or
 * accepts `descriptors` (descriptor injection stays internal/test-only;
 * a caller-injected permissive registry would let any action self-declare
 * aiCallable:true and bypass the CC-2a guard).
 */
export function resolveAiDispatchContext(
  state: EditorShellState,
  override: AiDispatchContext | undefined,
  base?: AiDispatchContext,
): AiDispatchContext {
  const merged = buildAiDispatchContext(state);
  // base fills gaps left by the structural derivation; defined-only so it
  // never clobbers a derived field with undefined.
  if (base !== undefined) {
    for (const key of Object.keys(base) as Array<keyof AiDispatchContext>) {
      assignDefinedContextField(merged, key, base[key]);
    }
  }
  // override wins over both derived + base, but ONLY when defined.
  if (override !== undefined) {
    for (const key of Object.keys(override) as Array<keyof AiDispatchContext>) {
      assignDefinedContextField(merged, key, override[key]);
    }
  }
  return merged;
}

function assignDefinedContextField<K extends keyof AiDispatchContext>(
  target: AiDispatchContext,
  key: K,
  value: AiDispatchContext[K],
): void {
  if (value !== undefined) target[key] = value;
}
